// Garen Judgment VFX: optional fast dash-in → full sword spin → fast dash-out.

import type { Animation } from "../../engine/animation/AnimationQueue";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";

const SPIN_MS = 350;
export const SPIN_COUNT = 6;
const SPIN_TOTAL = SPIN_MS * SPIN_COUNT;

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

export class SpinVFX implements Animation {
  private elapsed = 0;
  private spinsCompleted = 0;
  private cancelled = false;
  private readonly fullDuration: number;
  onEnd?: () => void;

  constructor(
    private center: Point,
    private radius: number,
    private color: string = "#c8a040",
    private onSpinComplete?: (spinIndex: number) => void,
    private visual?: UnitVisual,
    private spinCenter?: Point,
    private dashInMs: number = 180,
    private dashOutMs: number = 200,
  ) {
    this.fullDuration = spinCenter ? dashInMs + SPIN_TOTAL + dashOutMs : SPIN_TOTAL;
  }

  cancel(): void {
    this.cancelled = true;
  }

  update(dt: number): void {
    if (this.cancelled) return;
    this.elapsed = Math.min(this.elapsed + dt, this.fullDuration);

    if (this.visual && this.spinCenter) {
      const from = this.center;
      const to = this.spinCenter;
      if (this.elapsed < this.dashInMs) {
        const t = easeInOut(this.elapsed / this.dashInMs);
        this.visual.x = from.x + (to.x - from.x) * t;
        this.visual.y = from.y + (to.y - from.y) * t;
      } else if (this.elapsed < this.dashInMs + SPIN_TOTAL) {
        this.visual.x = to.x;
        this.visual.y = to.y;
      } else {
        const t = easeInOut((this.elapsed - this.dashInMs - SPIN_TOTAL) / this.dashOutMs);
        this.visual.x = to.x + (from.x - to.x) * t;
        this.visual.y = to.y + (from.y - to.y) * t;
      }
    }

    const spinElapsed = this.spinCenter ? Math.max(0, this.elapsed - this.dashInMs) : this.elapsed;
    const completedNow = Math.min(Math.floor(spinElapsed / SPIN_MS), SPIN_COUNT);
    while (this.spinsCompleted < completedNow) {
      this.onSpinComplete?.(this.spinsCompleted);
      this.spinsCompleted++;
    }
  }

  private getDrawCenter(): Point {
    if (!this.spinCenter) return this.center;
    const from = this.center;
    const to = this.spinCenter;
    if (this.elapsed < this.dashInMs) {
      const t = easeInOut(this.elapsed / this.dashInMs);
      return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    }
    if (this.elapsed < this.dashInMs + SPIN_TOTAL) return to;
    const t = easeInOut((this.elapsed - this.dashInMs - SPIN_TOTAL) / this.dashOutMs);
    return { x: to.x + (from.x - to.x) * t, y: to.y + (from.y - to.y) * t };
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.cancelled) return;

    const spinElapsed = this.spinCenter ? Math.max(0, this.elapsed - this.dashInMs) : this.elapsed;
    const spinT = Math.min(spinElapsed, SPIN_TOTAL) / SPIN_TOTAL;
    const angle = (Math.min(spinElapsed, SPIN_TOTAL) / SPIN_MS) * Math.PI * 2;

    const fadeStart = (SPIN_COUNT - 0.15) / SPIN_COUNT;
    let alpha = spinT > fadeStart ? 1 - (spinT - fadeStart) / (1 - fadeStart) : 1;

    // Fade in during dash-in, out during dash-out
    if (this.spinCenter) {
      if (this.elapsed < this.dashInMs) {
        alpha *= this.elapsed / this.dashInMs;
      } else if (this.elapsed > this.dashInMs + SPIN_TOTAL) {
        alpha *= 1 - (this.elapsed - this.dashInMs - SPIN_TOTAL) / this.dashOutMs;
      }
    }

    const center = this.getDrawCenter();
    ctx.save();
    ctx.translate(center.x, center.y);

    this.drawWindDisc(ctx, alpha);
    this.drawTrailArc(ctx, angle, alpha);
    this.drawSword(ctx, angle, alpha);

    ctx.restore();
  }

  private drawWindDisc(ctx: CanvasRenderingContext2D, alpha: number): void {
    const disc = ctx.createRadialGradient(0, 0, this.radius * 0.25, 0, 0, this.radius);
    disc.addColorStop(0, `rgba(200,160,64,0)`);
    disc.addColorStop(0.6, `rgba(200,160,64,${(0.07 * alpha).toFixed(3)})`);
    disc.addColorStop(1, `rgba(200,160,64,${(0.16 * alpha).toFixed(3)})`);
    ctx.fillStyle = disc;
    ctx.beginPath();
    ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawTrailArc(ctx: CanvasRenderingContext2D, angle: number, alpha: number): void {
    const TRAIL_ARC = Math.PI * 1.3;
    const STEPS = 22;
    const orbitR = this.radius * 0.85;

    for (let i = 0; i < STEPS; i++) {
      const frac = i / (STEPS - 1);
      const segStart = angle - TRAIL_ARC * (1 - frac);
      const segEnd = segStart + TRAIL_ARC / STEPS * 1.8;

      ctx.save();
      ctx.globalAlpha = alpha * frac * 0.45;
      ctx.strokeStyle = this.color;
      ctx.lineWidth = 2 + frac * 6;
      ctx.lineCap = "round";
      ctx.shadowColor = this.color;
      ctx.shadowBlur = frac * 12;
      ctx.beginPath();
      ctx.arc(0, 0, orbitR, segStart, segEnd);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawSword(ctx: CanvasRenderingContext2D, angle: number, alpha: number): void {
    const r = this.radius;
    const bladeStart = r * 0.18;
    const bladeTip = r;
    const bladeHalfW = 4.5;
    const guardX = r * 0.2;
    const guardW = 7;
    const guardH = 15;
    const handleX = r * 0.04;
    const handleLen = guardX - handleX;
    const handleH = 5;

    ctx.save();
    ctx.rotate(angle);
    ctx.globalAlpha = alpha;

    ctx.shadowColor = this.color;
    ctx.shadowBlur = 18;

    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.moveTo(bladeStart, -bladeHalfW);
    ctx.lineTo(bladeTip, 0);
    ctx.lineTo(bladeStart, bladeHalfW);
    ctx.closePath();
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.fillStyle = "#fffae0";
    ctx.beginPath();
    ctx.moveTo(bladeStart + 4, -1.5);
    ctx.lineTo(bladeTip - 2, 0);
    ctx.lineTo(bladeStart + 4, 1.5);
    ctx.closePath();
    ctx.fill();

    ctx.shadowColor = this.color;
    ctx.shadowBlur = 8;
    ctx.fillStyle = "#e8d070";
    ctx.fillRect(guardX - guardW * 0.5, -guardH * 0.5, guardW, guardH);

    ctx.shadowBlur = 0;
    ctx.fillStyle = "#7a3a10";
    ctx.fillRect(handleX + 2, -handleH * 0.5, handleLen - 2, handleH);

    ctx.fillStyle = this.color;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.arc(handleX, 0, 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  isDone(): boolean {
    return this.cancelled || this.elapsed >= this.fullDuration;
  }
}
